/**
 * Web Audio engine (08 §2 graph, §3 spatialisation, §5 voice management,
 * §7 options). Runs against `AudioContextLike` so the whole thing is
 * testable with `fake-context.ts`. Nothing here reads the render clock or
 * the sim; it is fed triggers and positions.
 */
import { VIRTUAL_W } from '../../../sim/constants.js'
import type { Arena, Team } from '../../../sim/types.js'
import { DEFAULT_CAMERA, project, type CameraParams } from '../camera.js'
import {
  AUDIO_BLUR_FADE_MS, AUDIO_BUS_DEFAULTS, AUDIO_BUS_RAMP_S, AUDIO_DEREZ_LOCAL_LPF_MS, AUDIO_DUCK_ATTACK_MS, AUDIO_DUCK_RELEASE_MS, AUDIO_FAR_LPF_HZ,
  AUDIO_LOOP_FADE_MS, AUDIO_MAX_VOICES, AUDIO_MAX_VOICES_REDUCED, AUDIO_MIN_DIST_GAIN, AUDIO_NEAR_LPF_HZ, AUDIO_PAN_WIDTH, AUDIO_PILEUP_ATTEN_DB,
  AUDIO_PILEUP_WINDOW_MS, AUDIO_REF_DIST, AUDIO_REVERB_SEND, AUDIO_ROUND_END_DUCK_DB, AUDIO_STEAL_FADE_MS, AUDIO_VOICE_DUCK_DB, AUDIO_VOICE_POOL, dbToGain, optionToGain,
} from './constants.js'
import type { AudioBufferLike, AudioBufferSourceNodeLike, AudioContextLike, BiquadFilterNodeLike, ConvolverNodeLike, DynamicsCompressorNodeLike, GainNodeLike, StereoPannerNodeLike } from './context.js'
import { SOUNDS, isAmbience, isBounce, isStem, type Bus, type Priority, type SoundId } from './manifest.js'
import { renderSound } from './synth.js'

export interface Pos2 { x: number; z: number }

export interface PlayOptions {
  pos?: Pos2
  entityKey?: string
  variation?: number
  gainMul?: number
  /** Seconds after "now" (08 §9: the sting after a hit starts 120 ms late). */
  startDelayS?: number
  /** Shared start time for time-aligned groups (double derez). */
  startAt?: number
  /** Buffer offset (s) to start from — phase-locked stems joining a running set. */
  offset?: number
  /** Loop playbackRate (disc whir). */
  rate?: number
  loop?: boolean
  /** Gain ramps 0 → target over this window from the start time (08 §6 stem crossfades). */
  fadeInMs?: number
}

interface Chain { gain: GainNodeLike; panner: StereoPannerNodeLike; filter: BiquadFilterNodeLike; busGain: GainNodeLike }

export interface Voice {
  id: SoundId
  pri: Priority
  bus: Bus
  spatial: boolean
  pos: Pos2 | null
  entity: string | null
  startedAt: number
  endsAt: number
  loop: boolean
  /** Distance gain × gainMul × pile-up; the panner/filter hold the spatial part. */
  baseGain: number
  distGain: number
  pan: number
  chain: Chain
  source: AudioBufferSourceNodeLike
  stopped: boolean
  variation: number
}

export interface VoiceHandle { voice: Voice; stop(fadeMs?: number): void }

/** The slice of `fetch` `preloadFiles` needs (injectable for tests). */
export type FetchLike = (url: string) => Promise<{ ok: boolean; arrayBuffer(): Promise<ArrayBuffer> }>
export interface FilesReport { loaded: SoundId[]; fellBack: SoundId[] }

export interface EngineDebug {
  unlocked: boolean
  voices: { id: string; pri: number; pan: number; gain: number; entity: string | null; startedAt: number; loop: boolean }[]
  buses: Record<Bus | 'master', number>
  muted: boolean
  reduced: boolean
  mono: boolean
  listenerTeam: Team
  musicState: string
  /** `n/m file-backed` (08 §8). */
  files: string
}

export interface EngineOptions { arena: Arena; listenerTeam?: Team }

export class AudioEngine {
  readonly ctx: AudioContextLike
  arena: Arena
  /** Active rig camera (spec §2): the listener rides the follow camera in the open world. */
  cam: CameraParams = DEFAULT_CAMERA
  listenerTeam: Team
  unlocked = false
  reduced = false
  mono = false
  muteOnBlur = true
  /** Label shown in the debug panel; set by the music machine. */
  musicState = '-'
  readonly buses: Record<Bus | 'master', GainNodeLike>
  readonly comp: DynamicsCompressorNodeLike
  readonly masterLpf: BiquadFilterNodeLike
  readonly sendReverb: GainNodeLike
  readonly convolver: ConvolverNodeLike
  /** Bus option values (0..1), before the v² curve; mute keeps these. */
  private busOption: Record<Bus | 'master', number> = { master: Math.sqrt(AUDIO_BUS_DEFAULTS.master), sfx: Math.sqrt(AUDIO_BUS_DEFAULTS.sfx), music: Math.sqrt(AUDIO_BUS_DEFAULTS.music), ui: Math.sqrt(AUDIO_BUS_DEFAULTS.ui), voice: Math.sqrt(AUDIO_BUS_DEFAULTS.voice) }
  private busMuted: Record<Bus | 'master', boolean> = { master: false, sfx: false, music: false, ui: false, voice: false }
  private blurred = false
  private pool: Chain[] = []
  private readonly voices: Voice[] = []
  private readonly loops = new Map<string, Voice>()
  private readonly lastPlayed = new Map<SoundId, number>()
  private readonly rr = new Map<SoundId, number>()
  private readonly bounceTimes: number[] = []
  private readonly buffers = new Map<string, AudioBufferLike>()
  /** Ids whose variation-0 buffer came from a shipped file (08 §8); the rest are live synth. */
  readonly fileBacked = new Set<SoundId>()
  /** Number of ids offered to `preloadFiles` (debug line). */
  filesTotal = 0
  private musicDucked = false
  private roundDuckDb = 0
  /** Music bus has two gain stages: option gain then duck gain. */
  private readonly musicDuck: GainNodeLike

  constructor(ctx: AudioContextLike, opts: EngineOptions) {
    this.ctx = ctx
    this.arena = opts.arena
    this.listenerTeam = opts.listenerTeam ?? 0
    const g = () => ctx.createGain()
    const master = g(), sfx = g(), music = g(), ui = g(), voice = g()
    this.musicDuck = g()
    this.comp = ctx.createDynamicsCompressor()
    this.comp.threshold.value = -12; this.comp.knee.value = 6; this.comp.ratio.value = 4; this.comp.attack.value = 0.003; this.comp.release.value = 0.12
    this.masterLpf = ctx.createBiquadFilter(); this.masterLpf.type = 'lowpass'; this.masterLpf.frequency.value = AUDIO_NEAR_LPF_HZ
    sfx.connect(master); ui.connect(master); voice.connect(master)
    music.connect(this.musicDuck); this.musicDuck.connect(master)
    master.connect(this.comp); this.comp.connect(this.masterLpf); this.masterLpf.connect(ctx.destination)
    this.sendReverb = g(); this.sendReverb.gain.value = AUDIO_REVERB_SEND
    this.convolver = ctx.createConvolver()
    sfx.connect(this.sendReverb); this.sendReverb.connect(this.convolver); this.convolver.connect(master)
    this.buses = { master, sfx, music, ui, voice }
    for (const b of Object.keys(this.buses) as (Bus | 'master')[]) this.buses[b].gain.value = optionToGain(this.busOption[b])
    for (let i = 0; i < AUDIO_VOICE_POOL; i++) this.pool.push(this.makeChain())
  }

  private makeChain(): Chain {
    const gain = this.ctx.createGain(), panner = this.ctx.createStereoPanner(), filter = this.ctx.createBiquadFilter()
    filter.type = 'lowpass'; filter.frequency.value = AUDIO_NEAR_LPF_HZ
    gain.connect(filter); filter.connect(panner)
    return { gain, panner, filter, busGain: this.buses.sfx }
  }

  // ── unlock / buffers ────────────────────────────────────────────────────

  /** 08 §9: first gesture resumes the context and plays the confirm tick. */
  async unlock(): Promise<void> {
    if (this.unlocked) return
    if (this.ctx.state !== 'running') await this.ctx.resume()
    this.unlocked = true
    // Reverb impulse: 0.9 s decaying noise (deterministic LCG, no Math.random).
    const sr = this.ctx.sampleRate, n = Math.round(0.9 * sr)
    const ir = this.ctx.createBuffer(2, n, sr)
    let s = 12345
    for (let ch = 0; ch < 2; ch++) { const d = ir.getChannelData(ch); for (let i = 0; i < n; i++) { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; d[i] = (s / 4294967296 * 2 - 1) * Math.exp(-i / (sr * 0.25)) * 0.5 } }
    this.convolver.buffer = ir
    this.play('unlock_tick')
  }

  private bufferFor(id: SoundId, variation: number): AudioBufferLike {
    const key = `${id}:${variation}`
    let b = this.buffers.get(key)
    if (!b) {
      const r = renderSound(id, variation, this.ctx.sampleRate)
      b = this.ctx.createBuffer(r.channels.length, r.channels[0].length, this.ctx.sampleRate)
      r.channels.forEach((ch, i) => { if (b!.copyToChannel) b!.copyToChannel(ch, i); else b!.getChannelData(i).set(ch) })
      this.buffers.set(key, b)
    }
    return b
  }

  /** Number of rendered buffers held (test/debug). */
  get bufferCount(): number { return this.buffers.size }

  /**
   * 08 §8 / 15 §7: fetch + decode the shipped files and swap them in as the
   * variation-0 buffer. Any failure (no decoder, fetch, decode, length off by
   * > 1 ms) leaves the id on its synth recipe and warns once. Reduced audio
   * skips the download entirely. Voices already playing keep their buffer.
   */
  async preloadFiles(files: Partial<Record<SoundId, string>>, fetchImpl: FetchLike = (url) => fetch(url)): Promise<FilesReport> {
    const report: FilesReport = { loaded: [], fellBack: [] }
    this.filesTotal = Object.keys(files).length
    if (this.reduced) return report
    const decode = this.ctx.decodeAudioData?.bind(this.ctx)
    const fallback = (id: SoundId, reason: string) => { report.fellBack.push(id); console.warn('[audio] fallback', id, reason) }
    for (const [id, url] of Object.entries(files) as [SoundId, string][]) {
      if (!decode) { fallback(id, 'no decodeAudioData'); continue }
      try {
        const res = await fetchImpl(url)
        if (!res.ok) { fallback(id, `fetch ${url}`); continue }
        const buf = await decode(await res.arrayBuffer())
        const want = SOUNDS[id].maxS
        if (Math.abs(buf.duration - want) > 0.001) { fallback(id, `length ${buf.duration.toFixed(4)} s ≠ ${want} s`); continue }
        this.buffers.set(`${id}:0`, buf)
        this.fileBacked.add(id)
        report.loaded.push(id)
      } catch (err) { fallback(id, err instanceof Error ? err.message : String(err)) }
    }
    return report
  }

  /** Spec §5 SOUND BANK: render variation 0 of each id ahead of the first gesture. Works on a
   *  suspended context (createBuffer needs no running clock). Yields to the event loop every
   *  8 sounds so the loading bar animates. */
  async prewarm(ids: readonly SoundId[], onProgress: (done01: number) => void): Promise<void> {
    let done = 0
    for (const id of ids) {
      this.bufferFor(id, 0)
      done++
      onProgress(done / Math.max(1, ids.length))
      if (done % 8 === 0) await new Promise<void>((r) => setTimeout(r, 0))
    }
    if (ids.length === 0) onProgress(1)
  }

  // ── spatialisation (08 §3) ──────────────────────────────────────────────

  setListenerTeam(team: Team): void { this.listenerTeam = team }
  setCamera(cam: CameraParams, arena: Arena): void { this.cam = cam; this.arena = arena }

  spatialise(pos: Pos2): { pan: number; gain: number; cutoff: number; zc: number } {
    const p = project(pos.x, 0, pos.z, this.listenerTeam, this.arena, this.cam)
    const zc = p.depth
    let pan = Math.max(-1, Math.min(1, (p.sx - VIRTUAL_W / 2) / (VIRTUAL_W / 2))) * AUDIO_PAN_WIDTH
    if (this.mono) pan = 0
    const gain = Math.max(AUDIO_MIN_DIST_GAIN, Math.min(1, AUDIO_REF_DIST / Math.max(zc, AUDIO_REF_DIST)))
    const f = Math.max(0, Math.min(1, (zc - AUDIO_REF_DIST) / 27))
    const cutoff = AUDIO_NEAR_LPF_HZ + (AUDIO_FAR_LPF_HZ - AUDIO_NEAR_LPF_HZ) * f
    return { pan, gain, cutoff, zc }
  }

  private applySpatial(v: Voice, smooth: boolean): void {
    const t = this.ctx.currentTime
    const s = v.spatial && v.pos ? this.spatialise(v.pos) : { pan: 0, gain: 1, cutoff: AUDIO_NEAR_LPF_HZ }
    v.pan = s.pan; v.distGain = s.gain
    if (smooth) {
      v.chain.panner.pan.setTargetAtTime(s.pan, t, 0.03)
      v.chain.filter.frequency.setTargetAtTime(s.cutoff, t, 0.03)
      v.chain.gain.gain.setTargetAtTime(v.baseGain * s.gain, t, 0.03)
    } else {
      v.chain.panner.pan.setValueAtTime(s.pan, t)
      v.chain.filter.frequency.setValueAtTime(s.cutoff, t)
      v.chain.gain.gain.setValueAtTime(v.baseGain * s.gain, t)
    }
  }

  /** Re-pan every live voice (spectator side switch, mono toggle). */
  retargetAll(): void { this.reap(); for (const v of this.voices) this.applySpatial(v, true) }

  // ── voices (08 §5) ──────────────────────────────────────────────────────

  get live(): Voice[] { this.reap(); return this.voices.slice() }
  get maxVoices(): number { return this.reduced ? AUDIO_MAX_VOICES_REDUCED : AUDIO_MAX_VOICES }

  private reap(): void {
    const t = this.ctx.currentTime
    for (let i = this.voices.length - 1; i >= 0; i--) {
      const v = this.voices[i]
      if (v.stopped || (!v.loop && t >= v.endsAt)) this.release(v, i)
    }
  }

  private release(v: Voice, index: number): void {
    v.chain.panner.disconnect()
    this.voices.splice(index, 1)
    if (v.entity && this.loops.get(v.entity) === v) this.loops.delete(v.entity)
    this.pool.push(v.chain)
  }

  private fadeOut(v: Voice, ms: number): void {
    if (v.stopped) return
    v.stopped = true
    const t = this.ctx.currentTime
    v.chain.gain.gain.cancelScheduledValues(t)
    v.chain.gain.gain.setValueAtTime(v.chain.gain.gain.value, t)
    v.chain.gain.gain.linearRampToValueAtTime(0, t + ms / 1000)
    v.source.stop(t + ms / 1000)
    v.endsAt = t + ms / 1000
    if (v.entity && this.loops.get(v.entity) === v) this.loops.delete(v.entity)
  }

  /** Pick a variation: round-robin, no immediate repeat (or the forced one). */
  private pickVariation(id: SoundId, forced?: number): number {
    const vars = SOUNDS[id].vars
    if (forced !== undefined) return ((forced % vars) + vars) % vars
    if (vars <= 1) return 0
    const last = this.rr.get(id)
    const next = last === undefined ? 0 : (last + 1) % vars
    this.rr.set(id, next)
    return next
  }

  /** Make room for a voice of priority `pri`. Returns false if it must be dropped. */
  private makeRoom(id: SoundId, pri: Priority): boolean {
    this.reap()
    const def = SOUNDS[id]
    // Per-id polyphony: steal the oldest of the same id.
    const same = this.voices.filter((v) => v.id === id && !v.stopped)
    if (same.length >= def.poly) { same.sort((a, b) => a.startedAt - b.startedAt); this.fadeOut(same[0], AUDIO_STEAL_FADE_MS) }
    const active = this.voices.filter((v) => !v.stopped && !isStem(v.id) && !isAmbience(v.id))
    if (active.length < this.maxVoices) return true
    // Global cap: lowest priority, then oldest; pri 3 never stolen.
    const stealable = active.filter((v) => v.pri < 3).sort((a, b) => a.pri - b.pri || a.startedAt - b.startedAt)
    if (stealable.length) { this.fadeOut(stealable[0], AUDIO_STEAL_FADE_MS); return true }
    if (pri < 3) return false
    const quietest = active.slice().sort((a, b) => a.baseGain * a.distGain - b.baseGain * b.distGain)[0]
    if (quietest) this.fadeOut(quietest, AUDIO_STEAL_FADE_MS)
    return true
  }

  play(id: SoundId, opts: PlayOptions = {}): VoiceHandle | null {
    const def = SOUNDS[id]
    if (!this.unlocked || def.reserved) return null
    const now = this.ctx.currentTime
    const nowMs = now * 1000
    if (this.reduced && (def.loop || isAmbience(id)) && !isStem(id)) return null
    // Cooldown (pri 3 exempt by construction).
    const last = this.lastPlayed.get(id)
    if (def.pri < 3 && def.cooldownMs > 0 && last !== undefined && nowMs - last < def.cooldownMs) return null
    // Bounce pile-up (08 §5).
    let pileup = 1
    if (isBounce(id)) {
      while (this.bounceTimes.length && nowMs - this.bounceTimes[0] > AUDIO_PILEUP_WINDOW_MS) this.bounceTimes.shift()
      this.bounceTimes.push(nowMs)
      if (this.bounceTimes.length > 2) pileup = dbToGain(AUDIO_PILEUP_ATTEN_DB)
    }
    if (!this.makeRoom(id, def.pri)) return null
    const chain = this.pool.pop() ?? this.makeChain()
    const variation = this.pickVariation(id, opts.variation)
    const buffer = this.bufferFor(id, variation)
    const src = this.ctx.createBufferSource()
    src.buffer = buffer
    const loop = opts.loop ?? def.loop
    if (loop) { src.loop = true; src.loopStart = 0; src.loopEnd = buffer.duration }
    if (opts.rate !== undefined) src.playbackRate.value = opts.rate
    chain.busGain = this.buses[def.bus]
    chain.panner.connect(chain.busGain)
    const startAt = opts.startAt ?? now + (opts.startDelayS ?? 0)
    const v: Voice = {
      id, pri: def.pri, bus: def.bus, spatial: def.spatial, pos: opts.pos ?? null, entity: opts.entityKey ?? null,
      startedAt: startAt, endsAt: loop ? Infinity : startAt + buffer.duration, loop,
      baseGain: (opts.gainMul ?? 1) * pileup, distGain: 1, pan: 0, chain, source: src, stopped: false, variation,
    }
    this.applySpatial(v, false)
    if (opts.fadeInMs) {
      const g = v.chain.gain.gain, target = g.value
      g.cancelScheduledValues(startAt)
      g.setValueAtTime(0, startAt)
      g.linearRampToValueAtTime(target, startAt + opts.fadeInMs / 1000)
    }
    if (opts.offset && opts.offset > 0) src.start(startAt, opts.offset); else src.start(startAt)
    this.voices.push(v)
    this.lastPlayed.set(id, nowMs)
    if (opts.entityKey && loop) { const old = this.loops.get(opts.entityKey); if (old && old !== v) this.fadeOut(old, AUDIO_LOOP_FADE_MS); this.loops.set(opts.entityKey, v) }
    return { voice: v, stop: (fadeMs = AUDIO_LOOP_FADE_MS) => this.fadeOut(v, fadeMs) }
  }

  /** Time-aligned group: every member starts at the same sample (08 §5). */
  playGroup(items: { id: SoundId; opts?: PlayOptions }[]): (VoiceHandle | null)[] {
    const startAt = this.ctx.currentTime
    return items.map((it) => this.play(it.id, { ...it.opts, startAt: startAt + (it.opts?.startDelayS ?? 0) }))
  }

  // ── loops by entity ─────────────────────────────────────────────────────

  hasLoop(key: string): boolean { this.reap(); return this.loops.has(key) }
  loopId(key: string): SoundId | null { return this.loops.get(key)?.id ?? null }

  startLoop(key: string, id: SoundId, pos?: Pos2, rate?: number, startAt?: number): VoiceHandle | null {
    const cur = this.loops.get(key)
    if (cur && cur.id === id && !cur.stopped) { this.updateLoop(key, pos, rate); return { voice: cur, stop: (ms = AUDIO_LOOP_FADE_MS) => this.fadeOut(cur, ms) } }
    if (cur) this.fadeOut(cur, AUDIO_LOOP_FADE_MS)
    return this.play(id, { entityKey: key, pos, rate, loop: true, startAt })
  }

  updateLoop(key: string, pos?: Pos2, rate?: number): void {
    const v = this.loops.get(key)
    if (!v || v.stopped) return
    if (pos) { v.pos = pos; this.applySpatial(v, true) }
    if (rate !== undefined) v.source.playbackRate.setTargetAtTime(rate, this.ctx.currentTime, 0.03)
  }

  /** Extra gain on a loop (shield "ready" layer). */
  setLoopGain(key: string, mul: number): void { const v = this.loops.get(key); if (v && !v.stopped) { v.baseGain = mul; this.applySpatial(v, true) } }

  stopLoop(key: string, fadeMs = AUDIO_LOOP_FADE_MS): void { const v = this.loops.get(key); if (v) this.fadeOut(v, fadeMs) }
  stopAllLoops(fadeMs = AUDIO_LOOP_FADE_MS): void { for (const k of [...this.loops.keys()]) this.stopLoop(k, fadeMs) }
  stopLoopsMatching(pred: (key: string, id: SoundId) => boolean, fadeMs = AUDIO_LOOP_FADE_MS): void { for (const [k, v] of [...this.loops]) if (pred(k, v.id)) this.fadeOut(v, fadeMs) }

  // ── buses / options (08 §2, §7) ─────────────────────────────────────────

  private busTarget(bus: Bus | 'master'): number {
    if (this.busMuted[bus]) return 0
    if (bus === 'master' && this.blurred && this.muteOnBlur) return 0
    return optionToGain(this.busOption[bus])
  }
  private rampBus(bus: Bus | 'master', tauS = AUDIO_BUS_RAMP_S): void { this.buses[bus].gain.setTargetAtTime(this.busTarget(bus), this.ctx.currentTime, tauS) }

  /** Options 0..1 → gain v² with a 20 ms ramp (A08-02). */
  setBusOption(bus: Bus | 'master', v: number): void { this.busOption[bus] = Math.max(0, Math.min(1, v)); this.rampBus(bus) }
  busOptionValue(bus: Bus | 'master'): number { return this.busOption[bus] }
  muteBus(bus: Bus | 'master', muted: boolean): void { this.busMuted[bus] = muted; this.rampBus(bus) }
  isMuted(bus: Bus | 'master'): boolean { return this.busMuted[bus] }

  /** Call-outs duck music −6 dB with 30/300 ms (A08-10). */
  duckMusic(on: boolean): void {
    if (on === this.musicDucked) return
    this.musicDucked = on
    this.applyMusicDuck(on ? AUDIO_DUCK_ATTACK_MS : AUDIO_DUCK_RELEASE_MS)
  }
  /** ROUND_END: stems to −12 dB (08 §6). `clear()` restores. */
  roundEndDuck(on: boolean): void { this.roundDuckDb = on ? AUDIO_ROUND_END_DUCK_DB : 0; this.applyMusicDuck(on ? 0 : 300) }
  private applyMusicDuck(ms: number): void {
    const db = (this.musicDucked ? AUDIO_VOICE_DUCK_DB : 0) + this.roundDuckDb
    const t = this.ctx.currentTime
    if (ms <= 0) this.musicDuck.gain.setValueAtTime(dbToGain(db), t)
    else this.musicDuck.gain.setTargetAtTime(dbToGain(db), t, ms / 1000 / 3)
  }
  get musicDuckDb(): number { return (this.musicDucked ? AUDIO_VOICE_DUCK_DB : 0) + this.roundDuckDb }

  /** Own derez: master LPF 18k → 2k → 18k over AUDIO_DEREZ_LOCAL_LPF_MS (A08-11). */
  derezLocalTail(): void {
    const t = this.ctx.currentTime, half = AUDIO_DEREZ_LOCAL_LPF_MS / 2000
    const f = this.masterLpf.frequency
    f.cancelScheduledValues(t)
    f.setValueAtTime(AUDIO_NEAR_LPF_HZ, t)
    f.linearRampToValueAtTime(2000, t + half)
    f.linearRampToValueAtTime(AUDIO_NEAR_LPF_HZ, t + 2 * half)
  }

  setReduced(on: boolean): void {
    this.reduced = on
    this.sendReverb.gain.setTargetAtTime(on ? 0 : AUDIO_REVERB_SEND, this.ctx.currentTime, AUDIO_BUS_RAMP_S)
    if (on) this.stopLoopsMatching((_k, id) => !isStem(id))
  }
  setMono(on: boolean): void { this.mono = on; this.retargetAll() }

  blur(): void { this.blurred = true; if (this.muteOnBlur) this.rampBus('master', AUDIO_BLUR_FADE_MS / 1000 / 3) }
  focus(): void { this.blurred = false; this.rampBus('master', AUDIO_BLUR_FADE_MS / 1000 / 3) }

  /** Hard resync (08 §9): every loop gone; the director rebuilds from the snapshot. */
  resetLoops(): void { this.stopAllLoops() }

  debug(): EngineDebug {
    this.reap()
    const buses = {} as Record<Bus | 'master', number>
    for (const b of Object.keys(this.buses) as (Bus | 'master')[]) buses[b] = this.buses[b].gain.value
    return {
      unlocked: this.unlocked,
      voices: this.voices.filter((v) => !v.stopped).map((v) => ({ id: v.id, pri: v.pri, pan: v.pan, gain: v.baseGain * v.distGain, entity: v.entity, startedAt: v.startedAt, loop: v.loop })),
      buses, muted: this.busMuted.master || (this.blurred && this.muteOnBlur), reduced: this.reduced, mono: this.mono, listenerTeam: this.listenerTeam, musicState: this.musicState,
      files: `${this.fileBacked.size}/${this.filesTotal} file-backed`,
    }
  }
}
